import React from 'react';
import type {
    NavItem,
    SideNavProps,
    LinkRenderProps,
} from '../../types/navigation';
import { cn } from '../../utils/cn';
import { Badge } from '../badge';
import { SideNavAnimation } from './side-nav-animation';

/**
 * Default link renderer that outputs basic a tags
 */
const DefaultLinkRenderer = ({
    to,
    children,
    isActive,
    className,
}: LinkRenderProps) => (
    <a
        href={to}
        className={cn(
            'block text-lg font-medium text-gray-900 hover:underline hover:decoration-2 hover:underline-offset-2',
            isActive &&
                '[&>span]:underline [&>span]:decoration-2 [&>span]:underline-offset-2',
            className,
        )}
    >
        {children}
    </a>
);

/**
 * Back button component for nested navigation
 */
const BackButton = ({
    renderLink,
    backItem,
    onBack,
}: {
    renderLink: (props: LinkRenderProps) => React.ReactNode;
    backItem: NavItem;
    onBack?: () => void;
}) => {
    /**
     * Intercept in the capture phase so we can prevent the inner
     * link (e.g. Remix NavLink) from performing its own navigation.
     * The link is still rendered for accessibility (right-click, keyboard nav).
     */
    const handleClickCapture = (e: React.MouseEvent) => {
        if (onBack) {
            e.preventDefault();
            e.stopPropagation();
            onBack();
        }
    };

    return (
        <div
            onClickCapture={handleClickCapture}
            className="flex h-15 items-center"
        >
            {renderLink({
                to: backItem.path,
                isActive: false,
                item: backItem,
                className:
                    'flex items-center text-dropdown text-gray-900 hover:text-gray-600 font-medium',
                children: (
                    <>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-1 h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fillRule="evenodd"
                                d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z"
                                clipRule="evenodd"
                            />
                        </svg>
                        <span>{backItem.label}</span>
                    </>
                ),
            })}
        </div>
    );
};

/**
 * Navigation item component
 */
const NavItemComponent = ({
    item,
    isActive = false,
    renderLink,
    onNavigate,
    depth = 0,
    isSecondary = false,
}: {
    item: NavItem;
    isActive?: boolean;
    renderLink: (props: LinkRenderProps) => React.ReactNode;
    onNavigate?: (item: NavItem) => void;
    depth?: number;
    isSecondary?: boolean;
}) => {
    const handleClick = () => {
        if (onNavigate && !item.disabled) {
            onNavigate(item);
        }
    };

    // Use secondary styling for secondary nav items
    const className = isSecondary
        ? 'inline text-3.5 text-gray-500 hover:text-gray-900'
        : undefined;

    return (
        <div
            className={cn(
                'flex flex-row items-center gap-2',
                depth > 0 && 'pl-4',
            )}
            onClick={handleClick}
        >
            {renderLink({
                to: item.path,
                isActive,
                item,
                className,
                children: (
                    <>
                        {item.icon && (
                            <span className="mr-2">
                                {typeof item.icon === 'string' ? (
                                    <span>{item.icon}</span>
                                ) : (
                                    React.createElement(item.icon)
                                )}
                            </span>
                        )}
                        <span>{item.label}</span>
                    </>
                ),
            })}

            {!isSecondary && item.isNew && (
                <Badge color="default" text="small" size="narrow">
                    NEW
                </Badge>
            )}
        </div>
    );
};

/**
 * Navigation list component for rendering nav items
 */
const NavList = ({
    items,
    renderLink,
    onNavigate,
    isSecondary = false,
}: {
    items: NavItem[];
    renderLink: (props: LinkRenderProps) => React.ReactNode;
    onNavigate?: (item: NavItem) => void;
    isSecondary?: boolean;
}) => (
    <ul className="space-y-4">
        {items.map((item) => {
            const hasChildren = item.children && item.children.length > 0;

            return (
                <li
                    key={item.id}
                    onClick={
                        hasChildren
                            ? () => onNavigate && onNavigate(item)
                            : undefined
                    }
                >
                    <NavItemComponent
                        item={item}
                        isActive={item.isActive}
                        renderLink={renderLink}
                        onNavigate={!hasChildren ? onNavigate : undefined}
                        isSecondary={isSecondary}
                    />
                </li>
            );
        })}
    </ul>
);

/**
 * SideNav component
 *
 * A flexible sidebar navigation component that supports:
 * - Multi-level navigation
 * - Custom link rendering for different routing libraries
 * - Custom header and footer
 * - Custom navigation content
 * - Loading states
 */
export const SideNav = ({
    items,
    secondaryItems,
    renderLink = DefaultLinkRenderer,
    header,
    footer,
    customNavContent,
    onNavigate,
    parentItem,
    onBack,
    isLoading = false,
    className,
}: SideNavProps) => {
    // Special handling for logout action in secondary navigation
    const handleLogoutClick = (item: NavItem) => {
        if (item.id === 'log-out' && onNavigate) {
            onNavigate(item);
        }
    };

    // If loading, show skeleton UI
    if (isLoading) {
        return (
            <aside
                className={cn('flex w-64 flex-col gap-11.5 pt-0.5', className)}
            >
                <div className="h-12 animate-pulse rounded bg-gray-200"></div>
                <nav>
                    <ul className="space-y-4">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <li
                                key={i}
                                className="h-6 animate-pulse rounded bg-gray-200"
                            ></li>
                        ))}
                    </ul>
                </nav>
            </aside>
        );
    }

    // Create back button element if we have a parent item
    const backButtonElement = parentItem ? (
        <BackButton
            renderLink={renderLink}
            backItem={parentItem}
            onBack={onBack}
        />
    ) : null;

    // Main navigation content
    const navigationContent = (
        <NavList
            items={items}
            renderLink={renderLink}
            onNavigate={onNavigate}
        />
    );

    // Secondary navigation content
    const secondaryNavContent =
        secondaryItems && secondaryItems.length > 0 ? (
            <nav>
                <ul className="space-y-2 pb-4">
                    {secondaryItems.map((item) => {
                        // Check if this is the logout item and handle it specially
                        if (item.id === 'log-out') {
                            return (
                                <li key={item.id}>
                                    <div
                                        className="inline cursor-pointer text-3.5 text-gray-500 hover:text-gray-900"
                                        onClick={() => handleLogoutClick(item)}
                                    >
                                        {item.label}
                                    </div>
                                </li>
                            );
                        }

                        return (
                            <li key={item.id}>
                                <NavItemComponent
                                    item={item}
                                    isActive={item.isActive}
                                    renderLink={renderLink}
                                    onNavigate={onNavigate}
                                    isSecondary={true}
                                />
                            </li>
                        );
                    })}
                </ul>
            </nav>
        ) : null;

    // Footer content
    const footerContent = footer ? <div>{footer}</div> : null;

    return (
        <aside className={cn('flex w-64 flex-col gap-11.5 pt-0.5', className)}>
            {/* Main Container */}
            {customNavContent ? (
                <div className="flex flex-1 flex-col gap-12">
                    {customNavContent}
                </div>
            ) : (
                <SideNavAnimation
                    hasParent={!!parentItem}
                    items={items}
                    header={header}
                    backButton={backButtonElement}
                    secondaryNav={secondaryNavContent}
                    footer={footerContent}
                >
                    {navigationContent}
                </SideNavAnimation>
            )}
        </aside>
    );
};
